import { useEffect, useRef, useState } from 'react';

const KEYBOARD_INSET_THRESHOLD_PX = 80;

const readViewport = () => {
  const viewport = window.visualViewport;
  return {
    height: viewport?.height ?? window.innerHeight,
    offsetTop: viewport?.offsetTop ?? 0,
    width: viewport?.width ?? window.innerWidth,
  };
};

// Live visual-viewport metrics: unlike dvh these shrink when the soft keyboard overlays the page.
export const useVisualViewportHeight = (active: boolean) => {
  const [viewport, setViewport] = useState(() => ({ ...readViewport(), isKeyboardOpen: false }));
  const baselineRef = useRef(readViewport());

  useEffect(() => {
    if (!active) return undefined;
    const update = () => {
      const next = readViewport();
      const baseline = baselineRef.current;
      // Baseline = tallest height per orientation; innerHeight also shrinks under adjustResize.
      const rebaselined = next.width !== baseline.width || next.height > baseline.height ? next : baseline;
      baselineRef.current = rebaselined;
      const inset = rebaselined.height - next.height;
      setViewport({ ...next, isKeyboardOpen: inset > KEYBOARD_INSET_THRESHOLD_PX });
    };
    update();
    const target = window.visualViewport;
    if (!target) {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    target.addEventListener('resize', update);
    target.addEventListener('scroll', update);
    return () => {
      target.removeEventListener('resize', update);
      target.removeEventListener('scroll', update);
    };
  }, [active]);

  return viewport;
};
